import type { BoggleAchievementRequirement } from '~/boggle/config/achievements/types'
import type {
  BoggleKnownAchievement,
  BoggleKnownAchievementTier,
} from '~/boggle/lib/achievements/knownAchievements'

export function formatAchievementProgress(current: number, target: number) {
  return `${Math.min(current, target).toLocaleString()} / ${target.toLocaleString()}`
}

function romanizeTier(index: number) {
  const labels = ['I', 'II', 'III', 'IV', 'V']
  return labels[index] ?? `${index + 1}`
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural
}

function requirementUnit(requirement: BoggleAchievementRequirement) {
  switch (requirement.type) {
    case 'all_badges_of_level':
      return `${requirement.level[0]?.toUpperCase()}${requirement.level.slice(1)} badges`
    case 'completed_runs':
      return 'runs'
    case 'first_badge_of_level':
      return 'badge'
    case 'long_words':
      return `words ${requirement.minLength}+ letters`
    case 'reached_rating':
      return 'rating'
    case 'single_run_word_count':
      return 'words in one run'
    case 'total_words':
      return 'words'
  }
}

export function isDiscreteRatingAchievement(achievement: BoggleKnownAchievement) {
  return achievement.definition.id === 'contender'
}

export function shouldShowTierPips(achievement: BoggleKnownAchievement) {
  return achievement.tiers.length > 1 && !isDiscreteRatingAchievement(achievement)
}

export function getActiveAchievementTier(achievement: BoggleKnownAchievement) {
  return (
    achievement.tiers.find(tier => !tier.completed) ??
    achievement.tiers[achievement.tiers.length - 1] ??
    null
  )
}

export function formatAchievementProgressLine({
  achievement,
  tier,
}: {
  achievement: BoggleKnownAchievement
  tier: BoggleKnownAchievementTier
}) {
  const tierIndex = achievement.tiers.findIndex(
    candidate => candidate.definition.id === tier.definition.id,
  )
  const label =
    achievement.tiers.length > 1 && achievement.definition.id !== 'badge-breakthrough'
      ? `Tier ${romanizeTier(tierIndex)}`
      : tier.definition.label
  const currentTarget = formatAchievementProgress(
    tier.progress.current,
    tier.progress.target,
  )
  return `${label} · ${currentTarget} ${requirementUnit(tier.definition.requirement)}`
}

export function formatAchievementNearMiss({
  achievement,
  tier,
}: {
  achievement: BoggleKnownAchievement
  tier: BoggleKnownAchievementTier
}) {
  if (tier.completed || tier.progress.target <= 0) {
    return null
  }
  if (tier.progress.current / tier.progress.target < 0.9) {
    return null
  }
  if (
    !['completed_runs', 'long_words', 'single_run_word_count', 'total_words'].includes(
      tier.definition.requirement.type,
    )
  ) {
    return null
  }
  const currentTarget = formatAchievementProgress(
    tier.progress.current,
    tier.progress.target,
  )
  return `So close - ${currentTarget} ${requirementUnit(tier.definition.requirement)}`
}

export function formatAchievementCompleteLine(achievement: BoggleKnownAchievement) {
  return `${achievement.completedTierCount} ${pluralize(
    achievement.completedTierCount,
    'tier',
  )} complete`
}
